import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Notification } from '../types';
import {
  BUSINESS_CHALLENGE_MINIMUM_NOTICE,
  BUSINESS_CHALLENGE_OFFICIAL_RULES_PATH,
} from '../utils/businessChallengeCopy';

type BusinessStage = 'new' | 'launching' | 'switching' | '';

type EntryFormState = {
  fullName: string;
  accountEmail: string;
  businessName: string;
  businessStage: BusinessStage;
  businessUrl: string;
  primaryService: string;
  progressStory: string;
  prizePlan: string;
  supportingUrl: string;
  privacyConsent: boolean;
};

const INITIAL_FORM: EntryFormState = {
  fullName: '',
  accountEmail: '',
  businessName: '',
  businessStage: '',
  businessUrl: '',
  primaryService: '',
  progressStory: '',
  prizePlan: '',
  supportingUrl: '',
  privacyConsent: false,
};

const CARD_STYLE: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(15, 23, 42, .10)',
  borderRadius: '18px',
  boxShadow: '0 10px 30px rgba(2, 6, 23, .06)',
};

const BusinessChallengePage: React.FC = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [isEntryOpen, setIsEntryOpen] = useState(false);
  const [form, setForm] = useState<EntryFormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      setUnreadNotifications([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.getNotifications();
        if (!cancelled) {
          setUnreadNotifications((response.data || []).filter((n: Notification) => !n.is_read));
        }
      } catch {
        if (!cancelled) setUnreadNotifications([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!isEntryOpen || !user) return;
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ').trim(),
      accountEmail: prev.accountEmail || user.email || '',
    }));
  }, [isEntryOpen, user]);

  useEffect(() => {
    if (!isEntryOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) setIsEntryOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isEntryOpen, submitting]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEntry = () => setIsEntryOpen(true);
  const closeEntry = () => {
    if (submitting) return;
    setIsEntryOpen(false);
  };

  const updateField = <K extends keyof EntryFormState>(key: K, value: EntryFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.businessStage) {
      toast.error('Please select which option best describes you');
      return;
    }
    if (!form.privacyConsent) {
      toast.error('Please agree to the Official Rules and Privacy Policy to continue');
      return;
    }

    try {
      setSubmitting(true);
      await apiService.submitBusinessChallengeEntry({
        challenge: 'build-your-business-2026',
        fullName: form.fullName.trim(),
        accountEmail: form.accountEmail.trim(),
        businessName: form.businessName.trim(),
        businessStage: form.businessStage,
        businessUrl: form.businessUrl.trim(),
        primaryService: form.primaryService.trim(),
        progressStory: form.progressStory.trim(),
        prizePlan: form.prizePlan.trim(),
        supportingUrl: form.supportingUrl.trim() || undefined,
        privacyConsent: true,
      });
      toast.success('Your challenge entry was submitted. Good luck!');
      setForm(INITIAL_FORM);
      setIsEntryOpen(false);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to submit your entry. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        showAuthButtons={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-8 md:py-12">
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div
              className="relative overflow-hidden p-6 md:p-10 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-center"
              style={CARD_STYLE}
            >
              <div
                className="pointer-events-none absolute top-0 right-0 h-56 w-56 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(109,94,252,.14), rgba(234,245,255,.38) 46%, transparent 70%)',
                  transform: 'translate(30%, -35%)',
                }}
                aria-hidden="true"
              />
              <div className="relative">
                <div className="flex flex-wrap gap-2 mb-4">
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide"
                    style={{ color: '#0B4778', backgroundColor: '#eaf5ff' }}
                  >
                    <i className="bi bi-trophy" aria-hidden="true"></i>
                    Business challenge
                  </span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide"
                    style={{ color: '#5146c8', backgroundColor: '#f0efff' }}
                  >
                    $3,000 in prizes
                  </span>
                </div>
                <h1
                  className="mb-4"
                  style={{
                    fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
                    fontWeight: 700,
                    color: '#0B4778',
                    lineHeight: 1.15,
                  }}
                >
                  Build your business with AACore Booking.
                </h1>
                <p className="mb-6 text-base md:text-lg" style={{ color: '#4b5563', lineHeight: 1.55 }}>
                  Start a freelance or small service business, or move your existing business to AACore.
                  Build your booking page, show us your progress, and enter for a chance to win.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <Link to="/business-registration/" className="aacore-btn-primary justify-center">
                    <i className="bi bi-plus-lg" aria-hidden="true"></i>
                    Create free business account
                  </Link>
                  <button
                    type="button"
                    onClick={openEntry}
                    className="aacore-btn-outlined justify-center"
                    disabled={loading}
                  >
                    <i className="bi bi-check2-circle" aria-hidden="true"></i>
                    Submit my entry
                  </button>
                </div>
                <p className="mt-4 mb-2 text-xs leading-relaxed" style={{ color: '#4b5563' }}>
                  {BUSINESS_CHALLENGE_MINIMUM_NOTICE}
                </p>
                <p className="mb-0 text-xs">
                  <Link
                    to={BUSINESS_CHALLENGE_OFFICIAL_RULES_PATH}
                    className="font-semibold underline"
                    style={{ color: '#0B4778' }}
                  >
                    Official Rules
                  </Link>
                </p>
              </div>

              <aside
                className="relative p-6 rounded-2xl"
                style={{
                  border: '1px solid rgba(109,94,252,.22)',
                  background: 'linear-gradient(145deg, #fbfbff, #f1f5ff)',
                }}
                aria-label="$3,000 total prize package"
              >
                <p
                  className="mb-1 uppercase tracking-widest"
                  style={{ fontSize: '.7rem', fontWeight: 700, color: '#5146c8', letterSpacing: '.1em' }}
                >
                  Total prize package
                </p>
                <p className="mb-4" style={{ fontSize: '2.75rem', fontWeight: 800, color: '#0B4778', lineHeight: 1 }}>
                  $3,000
                </p>
                <ul className="mb-0 space-y-2 list-none p-0">
                  {[
                    ['Grand Prize', '$1,500'],
                    ['Second Prize', '$750'],
                    ['Third Prize', '$500'],
                    ["People's Choice", '$250'],
                  ].map(([label, amount]) => (
                    <li
                      key={label}
                      className="flex justify-between gap-3 pt-2 text-sm"
                      style={{ borderTop: '1px solid rgba(109,94,252,.13)', color: '#4b5563' }}
                    >
                      <span>{label}</span>
                      <strong style={{ color: '#111827' }}>{amount}</strong>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          </div>
        </section>

        {/* Dates */}
        <section className="pb-8" aria-label="Challenge dates">
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div
              className="grid grid-cols-1 md:grid-cols-3 overflow-hidden"
              style={CARD_STYLE}
            >
              {[
                { icon: 'bi-plus-lg', label: 'Start today', value: 'Create or move your business' },
                { icon: 'bi-calendar-event', label: 'Entry deadline', value: 'November 15, 2026' },
                { icon: 'bi-star', label: 'Winners announced', value: 'Thanksgiving Day, November 26' },
              ].map((item, idx) => (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 p-4 md:p-5 ${
                    idx > 0 ? 'border-t md:border-t-0 md:border-l border-gray-200' : ''
                  }`}
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                    style={{ color: '#0B4778', backgroundColor: '#eaf5ff' }}
                    aria-hidden="true"
                  >
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <div>
                    <p className="mb-0.5 uppercase tracking-wide text-xs font-semibold" style={{ color: '#6b7280' }}>
                      {item.label}
                    </p>
                    <p className="mb-0 text-sm font-semibold" style={{ color: '#111827' }}>
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-10 md:py-14" id="how-it-works">
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="mb-2 uppercase tracking-widest text-xs font-bold" style={{ color: '#0B4778', letterSpacing: '.1em' }}>
                Three simple steps
              </p>
              <h2 className="mb-3 text-2xl md:text-3xl font-bold" style={{ color: '#0B4778' }}>
                Easy to enter. Built for real businesses.
              </h2>
              <p className="mb-0" style={{ color: '#6b7280' }}>
                You do not need a large audience or an established company. We want to see the useful business you can build.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  n: '01',
                  title: 'Create or move your business',
                  body: 'Open a free AACore Booking business account. Existing service businesses may switch and enter too.',
                  color: '#0B4778',
                  bg: '#eaf5ff',
                  link: true,
                },
                {
                  n: '02',
                  title: 'Build your booking page',
                  body: 'Add your services, schedule, photos, policies, and business details. Share the page and begin using it.',
                  color: '#5146c8',
                  bg: '#f0efff',
                  link: false,
                },
                {
                  n: '03',
                  title: 'Show us your progress',
                  body: 'Send your AACore business-page link and briefly explain what you built and what comes next.',
                  color: '#177245',
                  bg: '#eaf8f0',
                  link: false,
                  entry: true,
                },
              ].map((step) => (
                <article key={step.n} className="p-6" style={CARD_STYLE}>
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg mb-4 text-sm font-bold"
                    style={{ color: step.color, backgroundColor: step.bg }}
                  >
                    {step.n}
                  </div>
                  <h3 className="mb-2 text-lg font-semibold" style={{ color: '#111827' }}>
                    {step.title}
                  </h3>
                  <p className="mb-0 text-sm" style={{ color: '#6b7280' }}>
                    {step.body}
                  </p>
                  {step.link && (
                    <Link
                      to="/business-registration/"
                      className="inline-flex items-center gap-1 mt-4 text-sm font-semibold"
                      style={{ color: '#0B4778' }}
                    >
                      Create an account
                      <i className="bi bi-arrow-right text-xs" aria-hidden="true"></i>
                    </Link>
                  )}
                  {step.entry && (
                    <button
                      type="button"
                      onClick={openEntry}
                      className="aacore-btn-outlined mt-4 text-sm"
                    >
                      Open entry form
                      <i className="bi bi-chevron-right text-xs" aria-hidden="true"></i>
                    </button>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Review criteria */}
        <section className="py-10 md:py-14 bg-white border-y border-gray-200">
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center p-6 md:p-8" style={CARD_STYLE}>
              <div>
                <p className="mb-2 uppercase tracking-widest text-xs font-bold" style={{ color: '#0B4778', letterSpacing: '.1em' }}>
                  What we will review
                </p>
                <h2 className="mb-3 text-2xl md:text-3xl font-bold" style={{ color: '#0B4778' }}>
                  Progress matters more than where you started.
                </h2>
                <p className="mb-0" style={{ color: '#6b7280' }}>
                  We will focus on the work you put in and how ready your business is to serve customers, not follower count or business size.
                </p>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 list-none p-0 mb-0">
                {[
                  'Complete business page',
                  'Clear customer experience',
                  'Effort and improvement',
                  'Useful next-step plan',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 p-3.5 rounded-xl text-sm font-semibold"
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', color: '#111827' }}
                  >
                    <span
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs"
                      style={{ color: '#177245', backgroundColor: '#eaf8f0' }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-4 lg:px-8">
            <div
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 p-6 md:p-8 rounded-2xl"
              style={{
                border: '1px solid rgba(109,94,252,.2)',
                background: 'linear-gradient(135deg, #eef7ff, #f3f0ff)',
              }}
            >
              <div>
                <h2 className="mb-2 text-2xl md:text-3xl font-bold" style={{ color: '#0B4778' }}>
                  Your business can begin today.
                </h2>
                <p className="mb-0" style={{ color: '#4b5563' }}>
                  Create your free business account, build your page, and submit your progress by November 15.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Link to="/business-registration/" className="aacore-btn-primary justify-center">
                  <i className="bi bi-plus-lg" aria-hidden="true"></i>
                  Create account
                </Link>
                <button type="button" onClick={openEntry} className="aacore-btn-outlined justify-center">
                  <i className="bi bi-check2-circle" aria-hidden="true"></i>
                  Submit entry
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Entry modal */}
      {isEntryOpen && (
        <div
          className="fixed inset-0 z-[9998] flex items-center justify-center p-3 sm:p-4 bg-black/50"
          onClick={closeEntry}
          role="presentation"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="challenge-entry-title"
          >
            <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 shrink-0">
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
                  style={{ background: 'linear-gradient(145deg, #0B4778, #6d5efc)' }}
                  aria-hidden="true"
                >
                  <i className="bi bi-check-lg"></i>
                </div>
                <div className="min-w-0">
                  <h2 id="challenge-entry-title" className="text-lg sm:text-xl font-bold text-gray-900 mb-0.5">
                    Submit your challenge entry
                  </h2>
                  <p className="text-sm text-gray-500 mb-0">
                    About 5 minutes. Use the email connected to your AACore business account.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeEntry}
                disabled={submitting}
                className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 shrink-0"
                aria-label="Close submission form"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4 bg-gray-50">
                <div
                  className="flex gap-2.5 items-start p-3 rounded-xl text-sm"
                  style={{ backgroundColor: '#eaf5ff', border: '1px solid #d5eaff', color: '#274965' }}
                >
                  <span
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white text-xs font-bold"
                    style={{ backgroundColor: '#0B4778' }}
                    aria-hidden="true"
                  >
                    i
                  </span>
                  <p className="mb-0">
                    <strong>Before you submit:</strong> finish the public parts of your AACore business page and confirm that its link opens for customers.
                  </p>
                </div>

                {/* Section 1 */}
                <fieldset className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 m-0">
                  <legend className="px-1 text-base font-semibold text-gray-900 flex items-center gap-2">
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white"
                      style={{ backgroundColor: '#0B4778' }}
                    >
                      1
                    </span>
                    Your business
                  </legend>
                  <p className="text-xs text-gray-500 mb-4 mt-1">
                    Tell us who you are and connect the entry to your AACore business account.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="challenge-fullName" className="block text-sm font-medium text-gray-700 mb-1">
                        Full name <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="challenge-fullName"
                        required
                        maxLength={120}
                        value={form.fullName}
                        onChange={(e) => updateField('fullName', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                    </div>
                    <div>
                      <label htmlFor="challenge-accountEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        AACore account email <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="challenge-accountEmail"
                        type="email"
                        required
                        maxLength={180}
                        value={form.accountEmail}
                        onChange={(e) => updateField('accountEmail', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                    </div>
                    <div>
                      <label htmlFor="challenge-businessName" className="block text-sm font-medium text-gray-700 mb-1">
                        Business name <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="challenge-businessName"
                        required
                        maxLength={140}
                        value={form.businessName}
                        onChange={(e) => updateField('businessName', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                    </div>
                    <div>
                      <label htmlFor="challenge-stage" className="block text-sm font-medium text-gray-700 mb-1">
                        Which best describes you? <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="challenge-stage"
                        required
                        value={form.businessStage}
                        onChange={(e) => updateField('businessStage', e.target.value as BusinessStage)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                      >
                        <option value="" disabled>
                          Select one
                        </option>
                        <option value="new">Starting a new business</option>
                        <option value="launching">Preparing to launch</option>
                        <option value="switching">Existing business switching to AACore</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="challenge-businessUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        Public AACore business-page URL <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="challenge-businessUrl"
                        type="url"
                        required
                        maxLength={500}
                        placeholder="https://booking.aacore.net/your-business"
                        value={form.businessUrl}
                        onChange={(e) => updateField('businessUrl', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                      <p className="mt-1 text-xs text-gray-500 mb-0">
                        Open the link in a private window first to confirm customers can view it.
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="challenge-service" className="block text-sm font-medium text-gray-700 mb-1">
                        Primary service or business category <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="challenge-service"
                        required
                        maxLength={180}
                        placeholder="Example: hairstyling, photography, tutoring, home repair"
                        value={form.primaryService}
                        onChange={(e) => updateField('primaryService', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Section 2 */}
                <fieldset className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 m-0">
                  <legend className="px-1 text-base font-semibold text-gray-900 flex items-center gap-2">
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white"
                      style={{ backgroundColor: '#6d5efc' }}
                    >
                      2
                    </span>
                    Your progress
                  </legend>
                  <p className="text-xs text-gray-500 mb-4 mt-1">
                    Keep it practical. We care about what you built, improved, and plan to do next.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="challenge-progress" className="block text-sm font-medium text-gray-700 mb-1">
                        What have you built or improved with AACore Booking? <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="challenge-progress"
                        required
                        maxLength={800}
                        rows={4}
                        placeholder="Tell us where you started, what you completed, and how customers can now book or work with you."
                        value={form.progressStory}
                        onChange={(e) => updateField('progressStory', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                      <div className="flex justify-between gap-2 mt-1 text-xs text-gray-500">
                        <span>A clear, specific answer is best.</span>
                        <span>{form.progressStory.length} / 800</span>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="challenge-prizePlan" className="block text-sm font-medium text-gray-700 mb-1">
                        How would the prize help your next step? <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="challenge-prizePlan"
                        required
                        maxLength={600}
                        rows={3}
                        placeholder="Briefly describe the next practical investment you would make in your business."
                        value={form.prizePlan}
                        onChange={(e) => updateField('prizePlan', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                      <div className="flex justify-between gap-2 mt-1 text-xs text-gray-500">
                        <span>Focus on one or two realistic uses.</span>
                        <span>{form.prizePlan.length} / 600</span>
                      </div>
                    </div>
                  </div>
                </fieldset>

                {/* Section 3 */}
                <fieldset className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 m-0">
                  <legend className="px-1 text-base font-semibold text-gray-900 flex items-center gap-2">
                    <span
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white"
                      style={{ backgroundColor: '#177245' }}
                    >
                      3
                    </span>
                    Final check
                  </legend>
                  <p className="text-xs text-gray-500 mb-4 mt-1">
                    Add one optional supporting link, review the Official Rules and privacy notice, and submit when ready.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="challenge-supportingUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        Optional video, portfolio, or social link
                      </label>
                      <input
                        id="challenge-supportingUrl"
                        type="url"
                        maxLength={500}
                        placeholder="https://"
                        value={form.supportingUrl}
                        onChange={(e) => updateField('supportingUrl', e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]"
                      />
                      <p className="mt-1 text-xs text-gray-500 mb-0">
                        One public link only. A short 30 to 60 second introduction is welcome but not required.
                      </p>
                    </div>
                    <p className="text-xs leading-relaxed text-gray-600 mb-0">
                      {BUSINESS_CHALLENGE_MINIMUM_NOTICE}{' '}
                      <Link
                        to={BUSINESS_CHALLENGE_OFFICIAL_RULES_PATH}
                        target="_blank"
                        className="font-semibold underline"
                        style={{ color: '#0B4778' }}
                      >
                        Official Rules
                      </Link>
                    </p>
                    <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={form.privacyConsent}
                        onChange={(e) => updateField('privacyConsent', e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-[#0B4778] focus:ring-[#0B4778]"
                      />
                      <span>
                        I confirm that the information is accurate and that I have read and agree to the{' '}
                        <Link
                          to={BUSINESS_CHALLENGE_OFFICIAL_RULES_PATH}
                          target="_blank"
                          className="font-semibold underline"
                          style={{ color: '#0B4778' }}
                        >
                          Official Rules
                        </Link>
                        , including the 100-entry minimum participation requirement, and the{' '}
                        <Link to="/privacy" target="_blank" className="font-semibold underline" style={{ color: '#0B4778' }}>
                          Privacy Policy
                        </Link>
                        . <span className="text-red-600">*</span>
                      </span>
                    </label>
                  </div>
                </fieldset>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white shrink-0">
                <p className="mb-0 text-xs text-gray-500">Required fields are marked with an asterisk.</p>
                <div className="flex gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={closeEntry}
                    disabled={submitting}
                    className="aacore-btn-outlined flex-1 sm:flex-none justify-center"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="aacore-btn-primary flex-1 sm:flex-none justify-center disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg" aria-hidden="true"></i>
                        Submit challenge entry
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessChallengePage;
